import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Card, Empty, Flex, List, Space, Typography } from 'antd'
import { useEffect } from 'react'

import { FileOutlined } from '@ant-design/icons'
import { Menu as MenuModel } from '@models/Menu'
import { DndGrid } from '@pages/venues/detail/tabs/DndGrid'
import { reorderMenus } from '@services/MenuService'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getMenus, selectMenus } from '@store/MenuReducer'
import { selectSelectedVenue } from '@store/VenueReducer'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import { MenuAvatar } from './Avatar'

const { Title } = Typography

const MenuCard = () => {
  const { isOpen } = useSBRM()
  const dispatch = useAppDispatch()
  const menus = useAppSelector(selectMenus())
  const venue = useAppSelector(selectSelectedVenue())

  const fetchMenus = () => {
    if (!venue) return
    const query: TableParams = {
      ...initialQuery,
      columnKey: 'order',
      order: 'ascend',
      filters: { venues: [venue.id] },
      pagination: { ...initialQuery.pagination, pageSize: 1000 },
    }
    dispatch(getMenus(venue.slug, query))
  }

  useEffect(() => {
    if (isOpen) return
    fetchMenus()
  }, [venue, isOpen])

  useEffect(() => {
    fetchMenus()
  }, [venue])

  return (
    <Flex vertical className={'w-full'} gap={8}>
      <Space className="w-full space-between">
        <Title level={5} className="m-0">
          <FileOutlined /> Menus
        </Title>
        <Space>
          <SBRM.Actions
            entity={Entity.menu}
            actions={[SBRMAction.Create]}
            metadata={[{ key: MetadataKey.VenueSlug, value: venue?.slug }]}
          />
        </Space>
      </Space>

      <Flex vertical>
        <Card styles={{ body: { padding: 10 } }}>
          {menus.length == 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={'No menu to display'}
            />
          ) : (
            <List>
              <DndGrid<MenuModel>
                items={menus}
                styles={{
                  idle: { cursor: 'move' },
                  dragging: { opacity: 0.4 },
                  over: { opacity: 0.4 },
                }}
                onItemsChange={(updatedMenus: MenuModel[]) => {
                  const nextOrderData = updatedMenus.map(
                    (m: MenuModel, i: number) => ({
                      id: m.id,
                      order: i,
                    })
                  )
                  reorderMenus(venue!.slug, nextOrderData).then((_) =>
                    fetchMenus()
                  )
                }}
                renderItem={(item: any, style: React.CSSProperties, ref) => (
                  <List.Item ref={ref} style={{ padding: 10 }}>
                    <Space
                      className="w-full space-between"
                      style={{ padding: 5, cursor: 'move' }}
                    >
                      <MenuAvatar id={item.id} />
                      <SBRM.Actions
                        entity={Entity.menu}
                        entityId={item.id}
                        actions={[SBRMAction.Update, SBRMAction.Delete]}
                        metadata={[
                          { key: MetadataKey.VenueSlug, value: venue?.slug },
                        ]}
                      />
                    </Space>
                  </List.Item>
                )}
              />
            </List>
          )}
        </Card>
      </Flex>
    </Flex>
  )
}

type MenuCardType = { Card: typeof MenuCard }

export { MenuCard, type MenuCardType }
